// Tenant identity always comes from the session JWT, never a route/query/body tenantId.
export function createTenantAdminApi(http) {
  return {
    organization: () => http.get('/api/tenant/organization').then(r => r.data || null),
    subscription: () => http.get('/api/tenant/subscription').then(r => r.data || null),
    employees: status => http.get('/api/tenant/users', { params: { status } }).then(r => r.data || null),
    createEmployee: ({ email, displayName }) => http.post('/api/tenant/users', { email, displayName }).then(r => r.data),
    setEmployeeStatus: (userId, status) => http.patch(`/api/tenant/users/${encodeURIComponent(userId)}/status`, { status }),
    ssoStatus: () => http.get('/api/tenant/sso/status').then(({ data }) => data ? ({
      configured: data.configured, enabled: data.enabled, lastTestedAt: data.lastTestedAt,
      issuer: data.issuer, scopes: data.scopes,
    }) : null),
    saveSso: ({ issuer, clientId, clientSecret, scopes }) => http.post('/api/tenant/sso', { issuer, clientId, clientSecret, scopes }).then(() => undefined),
    testSso: () => http.post('/api/tenant/sso/test').then(r => ({ success: r.data.success, message: r.data.message })),
    ssoReadiness: () => http.get('/api/tenant/sso/readiness').then(({ data }) => ({ currentUserOidcProvisioned: data.currentUserOidcProvisioned, readyForSso: data.readyForSso, reason: data.reason })),
    provisionOidc: userId => http.post(`/api/tenant/users/${encodeURIComponent(userId)}/oidc-identity`).then(() => undefined),
  };
}

const messages = {
  INVALID_EMPLOYEE_DOMAIN: 'Employee email must match the organization’s primary domain.',
  USER_ALREADY_ACTIVE: 'An active employee with this email already exists. Use the existing record.',
  USER_ALREADY_DEACTIVATED: 'This employee is deactivated. Open the Deactivated view and reactivate the existing record.',
  SEAT_LIMIT_REACHED: 'No purchased seats are available. Deactivate an employee or contact Cobygo about additional seats.',
  SELF_DEACTIVATION_NOT_ALLOWED: 'You cannot change your own status.',
  USER_NOT_FOUND: 'The employee was not found. Refresh the list and try again.',
  TENANT_NOT_ACTIVE: 'The tenant or its subscription is not active. Contact Cobygo for assistance.',
  TENANT_ADMIN_REQUIRED: 'Tenant Admin access is required.',
  SSO_NOT_CONFIGURED: 'Save an SSO configuration before testing.',
  SSO_PROVIDER_DISABLED: 'The default OIDC provider must be enabled.',
  SSO_PROVIDER_INVALID: 'The default provider must use OIDC.',
  SSO_PROVIDER_NOT_TESTED: 'Test the saved configuration before enabling SSO for a user.',
  SSO_ADMIN_COUNT_INVALID: 'Exactly one active Tenant Admin is required before enabling SSO.',
  SSO_ADMIN_IDENTITY_REQUIRED: 'Enable SSO for the active Tenant Admin before changing the tenant authentication mode.',
  SSO_IDENTITY_CONFLICT: 'An existing OIDC identity conflicts with the default provider. Contact Cobygo support.',
  USER_NOT_ACTIVE: 'Only active tenant users can be enabled for SSO.',
  SSO_CONFIGURATION_INVALID: 'Check the SSO configuration. The issuer must be a valid HTTPS URL and a client ID and secret are required.',
  INVALID_STATUS: 'The requested employee status is not supported.',
};

export const ssoReadinessMessage = code => messages[code] || 'SSO readiness could not be confirmed. Reload and try again.';

// Translate known backend business codes. Never echo exception/provider payloads,
// since a failed configuration request can contain sensitive input.
export function adminError(error) {
  const data = error?.response?.data;
  const code = data?.code === 'AUTH_REQUEST_ERROR' ? String(data.message || '').split(':')[0] : data?.code;
  if (messages[code]) return messages[code];
  const status = error?.response?.status;
  if (status === 400) return 'Check the required fields and try again.';
  if (status === 401) return 'Your session has ended. Please sign in again.';
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 409) return 'The record changed or conflicts with an existing record. Refresh and try again.';
  if (status === 429) return 'Too many requests. Please wait before trying again.';
  return 'Unable to complete the request. Check your connection and try again.';
}
